/**
 * What a Contact Support message is and which part of the app it is about
 * (Drew, 2026-11-04: "if it's a bug, about downline management, I will know").
 * The form offers these; the Admin Panel groups tickets by section so a pile
 * about one screen can be read together ("17 messages about the export").
 *
 * The notify-support email keeps its own copy of both label maps
 * (supabase/functions/_shared/supportEmail.ts), since the browser bundle may
 * not import edge-function code; supportSection.test.ts pins the two equal.
 */

export interface SupportOption { value: string; label: string }

/** Offered on the form. Feature Request and Billing were taken off 2026-11-04. */
export const SUPPORT_CATEGORIES: SupportOption[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'calculation', label: 'Calculation' },
  { value: 'question', label: 'Question' },
  { value: 'comment', label: 'Comment' },
];

/** Older tickets carry these; they are labelled, never offered. */
const RETIRED_CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  billing: 'Billing',
  feature_request: 'Feature request',
};

export const SUPPORT_SECTIONS: SupportOption[] = [
  { value: 'wizard', label: 'Entering a client' },
  { value: 'import', label: '1040 import' },
  { value: 'strategies', label: 'Strategies' },
  { value: 'results', label: 'Results' },
  { value: 'comparison', label: 'Comparison page' },
  { value: 'export', label: 'Export (PDF or Excel)' },
  { value: 'clients', label: 'My Clients / All Clients' },
  { value: 'downline', label: 'Downline Management' },
  { value: 'walkthroughs', label: 'Walkthroughs' },
  { value: 'account', label: 'Signing in and account' },
  { value: 'other', label: 'Something else' },
];

/** The filter value for tickets sent before sections existed. */
export const NO_SECTION = 'none';

export function supportCategoryLabel(category: string | null | undefined): string {
  if (!category) return 'General';
  return SUPPORT_CATEGORIES.find(c => c.value === category)?.label
    ?? RETIRED_CATEGORY_LABELS[category]
    ?? 'General';
}

/** The section a person picks to type their own (Drew: "other with a fill out"). */
export const OTHER_SECTION = 'other';
/** How long the typed part may be; the database refuses longer. */
export const SECTION_DETAIL_MAX = 60;

/** "Downline Management", or "Something else: tax bracket" when they typed one. */
export function supportSectionLabel(section: string | null | undefined, detail?: string | null): string {
  if (!section) return 'No section';
  const label = SUPPORT_SECTIONS.find(s => s.value === section)?.label ?? section;
  const typed = section === OTHER_SECTION ? (detail ?? '').trim() : '';
  return typed ? `${label}: ${typed}` : label;
}

/** Each section that has tickets, with its count, biggest first; tickets with
 *  no section are counted under NO_SECTION and listed last. */
export function sectionCounts(tickets: { section?: string | null }[]): { value: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const t of tickets) {
    const key = t.section || NO_SECTION;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: value === NO_SECTION ? 'No section' : supportSectionLabel(value), count }))
    .sort((a, b) => (a.value === NO_SECTION ? 1 : 0) - (b.value === NO_SECTION ? 1 : 0) || b.count - a.count || a.label.localeCompare(b.label));
}

/** 'all' keeps every ticket; NO_SECTION keeps the ones sent before sections. */
export function filterBySection<T extends { section?: string | null }>(tickets: T[], section: string): T[] {
  if (section === 'all') return tickets;
  return tickets.filter(t => (t.section || NO_SECTION) === section);
}
